"use client";

import { useMemo } from "react";
import { devLabel } from "@/lib/dev-labels";
import {
	CATEGORIES,
	CATEGORY_LABELS,
	type ExportFormat,
	type FormatCategory,
	FORMATS,
	formatsByCategory,
} from "./formats";
import styles from "./ExportModal.module.css";

/**
 * FormatGrid — Phase 25-AB Task 12 (panel 20 · Format Grid).
 *
 * Mockup source: docs/mockups/export.html lines 269-541.
 * Renders 26 export formats grouped into 6 categories. Each card toggles
 * via checkbox + click anywhere on the card. The cost/tag chips on the
 * footer mirror the mockup CSS treatment.
 */

export interface FormatGridProps {
	selected: string[];
	onToggle: (key: string) => void;
	search?: string;
	onSearchChange?: (q: string) => void;
}

export function FormatGrid({
	selected,
	onToggle,
	search = "",
	onSearchChange,
}: FormatGridProps) {
	const grouped = useMemo(() => formatsByCategory(), []);
	const selectedSet = useMemo(() => new Set(selected), [selected]);
	const q = search.trim().toLowerCase();

	function matches(f: ExportFormat): boolean {
		if (!q) return true;
		return (
			f.label.toLowerCase().includes(q) ||
			f.ext.toLowerCase().includes(q) ||
			f.description.toLowerCase().includes(q)
		);
	}

	return (
		<section
			className={styles.formatGrid}
			data-testid="format-grid"
			aria-label="형식 선택"
		>
			<div className={styles.centerBar} {...devLabel("20-A")}>
				<h2 className={styles.h2}>
					형식 선택{" "}
					<span className={styles.subCount}>
						총 {FORMATS.length} 형식 / {selected.length} 선택됨
					</span>
				</h2>
				{onSearchChange && (
					<div className={styles.searchInput} {...devLabel("20-A1")}>
						<input
							type="text"
							placeholder="형식 검색 (예: CapCut, FCPXML, SRT...)"
							value={search}
							onChange={(e) => onSearchChange(e.target.value)}
							data-testid="format-search"
							aria-label="형식 검색"
						/>
					</div>
				)}
			</div>

			{CATEGORIES.map((cat: FormatCategory) => {
				const cards = grouped[cat].filter(matches);
				if (cards.length === 0) return null;
				return (
					<div key={cat} data-testid={`category-${cat}`}>
						<div className={styles.groupTitle}>
							<span>{CATEGORY_LABELS[cat]}</span>
							<span className={styles.groupCount}>
								{grouped[cat].length} 형식
							</span>
						</div>
						<div className={styles.fmtGrid}>
							{cards.map((f) => {
								const checked = selectedSet.has(f.key);
								return (
									<button
										type="button"
										key={f.key}
										className={[
											styles.fmtCard,
											checked ? styles.fmtChecked : "",
											f.tag === "popular" || f.tag === "mml"
												? styles.fmtFeatured
												: "",
										]
											.filter(Boolean)
											.join(" ")}
										onClick={() => onToggle(f.key)}
										aria-pressed={checked}
										data-testid={`format-${f.key}`}
										{...devLabel(f.dev)}
									>
										<div className={styles.fmtCheck} aria-hidden="true">
											{checked ? "✓" : ""}
										</div>
										<div className={styles.fmtHead}>
											<div className={styles.fmtName}>{f.label}</div>
											<div className={styles.fmtExt}>{f.ext}</div>
										</div>
										<div className={styles.fmtDesc}>{f.description}</div>
										<div className={styles.fmtFoot}>
											<span
												className={
													f.cost === "free"
														? styles.costFree
														: styles.costPaid
												}
											>
												{f.cost === "free" ? "무료" : `+${f.cost}`}
											</span>
											{f.tag && (
												<span
													className={[
														styles.fmtTag,
														f.tag === "popular"
															? styles.tagPopular
															: f.tag === "pro"
																? styles.tagPro
																: f.tag === "new"
																	? styles.tagNew
																	: styles.tagMml,
													].join(" ")}
												>
													{f.tag === "popular"
														? "추천"
														: f.tag === "pro"
															? "PRO"
															: f.tag === "new"
																? "신규"
																: "MML+"}
												</span>
											)}
										</div>
									</button>
								);
							})}
						</div>
					</div>
				);
			})}
		</section>
	);
}
